import React, { MouseEvent, useContext, useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Divider, IconButton, MenuList } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { reviseTicket, saveStalledTickets, TicketDatabase } from "../Utils/TicketDatabase";
import { GlobalContext } from "../Utils/Contexts";
import TicketValidator from "../Utils/TicketValidator";
import { TicketErrorHelper } from "../Utils/TicketErrorHelper";

import { bookmarkTicket, IsDemoMode } from "../sharedCode/HelperMethods";
import AboutDialog from "../Components/AboutDialog";
import ReinstallApplicationConfirmationDialog from "../Components/ReinstallApplicationConfirmationDialog";
import { useLiveQuery } from "dexie-react-hooks";
import { getTicketById, isNotInitializationTicket } from "../Utils/UtilityMethods";
import { isAdminUser, isSuperUser } from "../Components/validation/newHelpers";
import copy from "copy-to-clipboard";
import TextField from "@mui/material/TextField";

interface SettingsMenuProps {
    setAuthorizationToken(newToken: string): void;
    showForceReloadDialogue(): void;
}

export default function SettingsMenu(props: SettingsMenuProps): JSX.Element {
    const [anchorElement, setAnchorElement] = React.useState<HTMLButtonElement | null>(null);
    const [fallbackTest, setFallbackTest] = useState(false);
    const [showAbout, setShowAbout] = useState(false);
    const [showReinstallConfirmation, setShowReinstallConfirmation] = useState(false);
    const aircraftList = useLiveQuery(async () => TicketDatabase.aircraft.toArray());
    const ticketList = useLiveQuery(async () => TicketDatabase.ticket.toArray());
    const context = useContext(GlobalContext);

    if (fallbackTest) {
        // Triggers a react error by changing the hook order
        const [fallbackError, setFallbackError] = useState(false);
    }

    if (!aircraftList || !ticketList) return <></>;

    const ticket = getTicketById(ticketList, context.selectedTicketId);
    // Include tickets that still carry a partialSyncSnapshot even after ResetSyncCounts clears pushAttempts
    const stalledTickets = ticketList.filter((t) => t.pushAttempts > 0 || t.partialSyncSnapshot != null);

    const validationResults = TicketValidator.validate(aircraftList, ticketList, ticket);

    const open = Boolean(anchorElement);

    const canRevise = TicketValidator.CanRevise(ticketList ?? [], ticket);
    const enableClone = isNotInitializationTicket(ticket);
    const enableSignaturePending = isNotInitializationTicket(ticket) && TicketErrorHelper.isApprovable(validationResults) && !TicketValidator.IsReadOnly(ticket);

    const demoModeToggleText = IsDemoMode() ? "END DEMO MODE" : "DEMO MODE";

    const isAdmin = isAdminUser(context.loggedInUser);
    const isSuper = isSuperUser(context.loggedInUser);

    const handleClick = (event: MouseEvent) => {
        setAnchorElement(event.currentTarget as HTMLButtonElement);
    };

    const handleClose = () => {
        setAnchorElement(null);
    };

    const handleLogout = () => {
        setAnchorElement(null);
        props.setAuthorizationToken("");
    };

    const handleAbout = () => {
        setAnchorElement(null);
        setShowAbout(true);
    };

    const handleCloseAbout = () => {
        setShowAbout(false);
    };

    const handleReinstall = () => {
        setShowReinstallConfirmation(true);
        setAnchorElement(null);
    };

    function handleSetReinstallConfirmation(newValue: boolean) {
        setShowReinstallConfirmation(newValue);
    }

    const handleErrorTest = () => {
        setTimeout(() => {
            handleClose();
        }, 100);

        const fragment =
            "Error thrown to test remote logging using a really long error message that by default Sentry will apparently truncate. Funny that a logging solution wouldn't be littered with warnings that hey, we might truncate the information you're trying to log bur here were are";

        throw `${fragment} ${fragment} ${fragment} ${fragment}`;
    };

    const handleFallbackTest = () => {
        setFallbackTest(true);
    };

    const handleLoadTickets = () => {
        context.setShowLoadingTicketsDialogue(true);
        handleClose();
    };

    const handleShowReleaseNotes = () => {
        context.showReleaseNotes(true);
        handleClose();
    };

    const handleExportTicketData = () => {
        const dataToExport = JSON.stringify(stalledTickets).split('"id":').join('\r\n"id":'); // need to add line breaks to avoid crashing the email client
        copy(dataToExport);
        handleClose();
        window.location.href = "mailto:laverne.kelly@bayriverinc.com?subject=STALLED TICKET DATA&body=DOUBLE-TAP HERE AND PASTE, THEN SEND";
    };

    async function handleImportDataPasted(pastedData: string) {
        const stalledTickets = JSON.parse(pastedData); // json is nice enough to ignore line breaks, just copy the entire body and paste here
        await saveStalledTickets(stalledTickets);
        handleClose();
    }

    async function handleCreateRevision() {
        const revision = await reviseTicket(ticket).catch(() => props.showForceReloadDialogue());
        if (revision) {
            bookmarkTicket(revision);
            context.setSelectedTicketId(revision.id);
        }
        handleClose();
    }

    const handleCloneTicket = () => {
        context.cloneTicket(ticket);
        handleClose();
    };

    const handleShowUserPreferences = () => {
        context.showUserPreferences(true);
        handleClose();
    };

    const handleToggleDemoMode = () => {
        context.toggleDemoMode(context, isSuper); // TODO now that isSuperUser is a helper we can remove the parameter (and why am I passing context to itself??)
        handleClose();
    };

    const handleSignaturePending = () => {
        context.displaySignaturePendingDialogue();
        handleClose();
    };

    return (
        <>
            <IconButton size="large" edge="start" color="inherit" sx={{ ml: 2, mr: 2 }} onClick={handleClick}>
                <MenuIcon />
            </IconButton>
            {showReinstallConfirmation && <ReinstallApplicationConfirmationDialog setReinstallConfirmation={handleSetReinstallConfirmation} />}
            <Menu
                anchorEl={anchorElement}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button",
                }}>
                <MenuList>
                    <MenuItem disabled={!canRevise} onClick={handleCreateRevision}>
                        REVISE SELECTED TICKET
                    </MenuItem>
                    <MenuItem disabled={!enableClone} onClick={handleCloneTicket}>
                        CLONE SELECTED TICKET
                    </MenuItem>
                    <MenuItem disabled={!enableSignaturePending} onClick={handleSignaturePending}>
                        COMPLETE TICKET WITHOUT SIGNATURE
                    </MenuItem>
                    <MenuItem onClick={handleShowUserPreferences}>USER PREFERENCES</MenuItem>
                    <MenuItem onClick={handleToggleDemoMode}>{demoModeToggleText}</MenuItem>
                    <MenuItem onClick={handleLoadTickets}>LOAD TICKETS FROM RPS</MenuItem>
                    <MenuItem onClick={handleShowReleaseNotes}>RELEASE NOTES</MenuItem>
                    {stalledTickets.length > 0 && <MenuItem onClick={handleExportTicketData}>EMAIL STALLED TICKET DATA</MenuItem>}
                    <MenuItem onClick={handleLogout}>LOGOUT</MenuItem>
                    <MenuItem onClick={handleAbout}>ABOUT</MenuItem>
                    <Divider />
                    <MenuItem onClick={handleReinstall}>RE-INSTALL APPLICATION</MenuItem>
                    {isAdmin && (
                        <MenuList>
                            <MenuItem onClick={handleErrorTest}>TEST ERROR LOGGING</MenuItem>
                            <MenuItem onClick={handleFallbackTest}>TEST FALLBACK DIALOGUE</MenuItem>
                        </MenuList>
                    )}
                    {isAdmin && (
                        <TextField sx={{ marginLeft: 2 }} onChange={(e) => handleImportDataPasted(e.target.value)} helperText={"PASTE EMAILED DATA HERE"} multiline></TextField>
                    )}
                </MenuList>
            </Menu>
            {showAbout && <AboutDialog onClose={handleCloseAbout} />}
        </>
    );
}
